// ============================================================================
// Gmail — hand-rolled MIME builder + parser.
//
// Why not a dep?
//   Gmail needs RFC 2822 MIME on the send path + full parse on the receive
//   path. Popular npm MIME libs (mailparser, mimemessage) pull Node buffers
//   and ~50KB+ of code we'd be wedging into the Workers runtime. Our needs
//   are narrow — handle `multipart/mixed`, `multipart/alternative`,
//   `multipart/related`, `base64`, `quoted-printable`, UTF-8 charset, and
//   inline attachment Content-Id references. That's a small surface we can
//   cover correctly in ~400 lines.
//
// Scope:
//   BUILD: sendEmail input → RFC 2822 MIME text → base64url for Gmail's
//          `users.messages.send` `{ raw }` field.
//   PARSE: inbound `users.messages.get?format=full` returns base64url-encoded
//          raw MIME; we decode, parse headers + multiparts, and return
//          `EmailFull`. Attachment bodies are NOT inlined — Gmail returns
//          `attachmentId` references for parts > ~4KB, and callers use
//          `getAttachment(...)` to fetch the bytes.
//
// Thai text correctness:
//   Gmail stores all encoded parts verbatim. We MUST respect
//   `Content-Transfer-Encoding: base64|quoted-printable` and preserve the
//   `charset=utf-8` parameter. The QP decoder below handles `=E0=B8=81`
//   three-byte UTF-8 sequences (Thai code points U+0E00–U+0E7F) and the
//   `=XX` soft-line-break (`=\r\n`) per RFC 2045.
// ============================================================================

import type { EmailAddress, EmailAttachment, EmailFull, EmailSendInput } from '../../capabilities/email'

// ============================================================================
// base64url helpers (Gmail `raw` field requires url-safe base64 without `=`)
// ============================================================================

/**
 * Encode a raw MIME string as base64url. Gmail's `users.messages.send` body
 * accepts EITHER standard base64 OR base64url — we pick url-safe so the
 * value can also flow through URL-encoded channels (e.g. webhook debug
 * payloads) without extra transforms.
 */
export function base64UrlEncodeMessage(raw: string): string {
  const bytes = new TextEncoder().encode(raw)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Decode base64url → string (UTF-8). Tolerates missing padding. */
export function base64UrlDecodeToString(b64url: string): string {
  const bytes = base64UrlDecodeToBytes(b64url)
  return new TextDecoder('utf-8').decode(bytes)
}

/** Decode base64url → raw bytes. */
export function base64UrlDecodeToBytes(b64url: string): Uint8Array {
  const normalized = b64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  const binary = atob(padded)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

/** Standard base64 encode raw bytes. Used for attachments inside multipart. */
function base64EncodeBytes(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let binary = ''
  for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i])
  return btoa(binary)
}

/** Wrap a base64 string at 76 chars per RFC 2045 line-length rule. */
function wrapBase64(b64: string, width = 76): string {
  const out: string[] = []
  for (let i = 0; i < b64.length; i += width) {
    out.push(b64.slice(i, i + width))
  }
  return out.join('\r\n')
}

// ============================================================================
// Build — EmailSendInput → RFC 2822 MIME text
// ============================================================================

/**
 * Build an RFC 2822 MIME message string from the capability-level send input.
 *
 * Headers: `From`, `To`, `Cc`, `Bcc`, `Subject`, `Reply-To`, `In-Reply-To`,
 * `References`, `Date`, `MIME-Version: 1.0`. Subjects + non-ASCII names are
 * encoded with RFC 2047 `=?utf-8?B?...?=` to preserve Thai characters.
 *
 * Body strategy:
 *   - text only                  → `Content-Type: text/plain; charset=utf-8`
 *   - html only                  → `Content-Type: text/html; charset=utf-8`
 *   - text + html                → `multipart/alternative` with two parts
 *   - attachments present        → outer `multipart/mixed` wrapping the
 *                                  text/html part (or multipart/alternative)
 *                                  + one attachment part each.
 */
export function buildMimeMessage(input: EmailSendInput, sender?: EmailAddress): string {
  if (!input || !Array.isArray(input.to) || input.to.length === 0) {
    throw new Error('buildMimeMessage: input.to must have at least one recipient')
  }
  if (typeof input.subject !== 'string') {
    throw new Error('buildMimeMessage: input.subject is required')
  }
  if (!input.body || (!input.body.text && !input.body.html)) {
    throw new Error('buildMimeMessage: input.body must have text or html')
  }

  const boundary = generateBoundary()
  const mixedBoundary = generateBoundary()
  const attachments = Array.isArray(input.attachments) ? input.attachments : []
  const hasAttachments = attachments.length > 0
  const hasBothBodies = !!input.body.text && !!input.body.html

  // ── Headers
  const headers: string[] = []
  if (sender) headers.push(`From: ${formatAddress(sender)}`)
  headers.push(`To: ${input.to.map(formatAddress).join(', ')}`)
  if (input.cc && input.cc.length > 0) headers.push(`Cc: ${input.cc.map(formatAddress).join(', ')}`)
  if (input.bcc && input.bcc.length > 0) headers.push(`Bcc: ${input.bcc.map(formatAddress).join(', ')}`)
  if (input.replyTo && input.replyTo.length > 0) {
    headers.push(`Reply-To: ${input.replyTo.map(formatAddress).join(', ')}`)
  }
  headers.push(`Subject: ${encodeHeaderValue(input.subject)}`)
  headers.push(`Date: ${new Date().toUTCString()}`)
  headers.push(`MIME-Version: 1.0`)

  // Reply threading — callers supplying `replyToMessageId` get both headers.
  // The adapter populates these from the referenced message; the builder
  // doesn't look them up itself. For callers who want raw threading control
  // (e.g. bridging an external thread), they can pass the message-id strings
  // directly via the extended shape on `send.ts`.
  // NB: this builder does NOT read `input.replyToMessageId` — that's resolved
  // by send.ts which also injects the In-Reply-To / References headers.

  // ── Body
  if (!hasAttachments) {
    if (hasBothBodies) {
      headers.push(`Content-Type: multipart/alternative; boundary="${boundary}"`)
      return [
        ...headers,
        '',
        `--${boundary}`,
        ...buildTextPart(input.body.text!),
        '',
        `--${boundary}`,
        ...buildHtmlPart(input.body.html!),
        '',
        `--${boundary}--`,
        '',
      ].join('\r\n')
    }
    if (input.body.text) {
      headers.push(`Content-Type: text/plain; charset=utf-8`)
      headers.push(`Content-Transfer-Encoding: quoted-printable`)
      return [...headers, '', encodeQuotedPrintable(input.body.text), ''].join('\r\n')
    }
    // html only
    headers.push(`Content-Type: text/html; charset=utf-8`)
    headers.push(`Content-Transfer-Encoding: quoted-printable`)
    return [...headers, '', encodeQuotedPrintable(input.body.html!), ''].join('\r\n')
  }

  // With attachments — outer multipart/mixed.
  headers.push(`Content-Type: multipart/mixed; boundary="${mixedBoundary}"`)
  const lines: string[] = [...headers, '']

  lines.push(`--${mixedBoundary}`)
  if (hasBothBodies) {
    lines.push(`Content-Type: multipart/alternative; boundary="${boundary}"`)
    lines.push('')
    lines.push(`--${boundary}`)
    lines.push(...buildTextPart(input.body.text!))
    lines.push('')
    lines.push(`--${boundary}`)
    lines.push(...buildHtmlPart(input.body.html!))
    lines.push('')
    lines.push(`--${boundary}--`)
  } else if (input.body.text) {
    lines.push(...buildTextPart(input.body.text))
  } else {
    lines.push(...buildHtmlPart(input.body.html!))
  }
  lines.push('')

  for (const att of attachments) {
    lines.push(`--${mixedBoundary}`)
    lines.push(...buildAttachmentPart(att))
    lines.push('')
  }

  lines.push(`--${mixedBoundary}--`)
  lines.push('')
  return lines.join('\r\n')
}

function buildTextPart(text: string): string[] {
  return [
    `Content-Type: text/plain; charset=utf-8`,
    `Content-Transfer-Encoding: quoted-printable`,
    '',
    encodeQuotedPrintable(text),
  ]
}

function buildHtmlPart(html: string): string[] {
  return [
    `Content-Type: text/html; charset=utf-8`,
    `Content-Transfer-Encoding: quoted-printable`,
    '',
    encodeQuotedPrintable(html),
  ]
}

function buildAttachmentPart(att: {
  filename: string
  contentType: string
  data: ArrayBuffer
  contentId?: string
}): string[] {
  const b64 = base64EncodeBytes(att.data)
  const wrapped = wrapBase64(b64)
  const lines = [
    `Content-Type: ${att.contentType}; name="${escapeHeaderParam(att.filename)}"`,
    `Content-Disposition: attachment; filename="${escapeHeaderParam(att.filename)}"`,
    `Content-Transfer-Encoding: base64`,
  ]
  if (att.contentId) {
    lines.push(`Content-ID: <${att.contentId}>`)
    // `inline` disposition would be wrong for a true attachment — we keep
    // the attachment disposition above so Gmail renders the file in the
    // attachments strip as well as being referencable via `cid:`.
  }
  lines.push('')
  lines.push(wrapped)
  return lines
}

// ============================================================================
// Address formatting — RFC 5322 `name <email>` with RFC 2047 encoding when
// the display name contains non-ASCII (e.g. Thai).
// ============================================================================

function formatAddress(addr: EmailAddress): string {
  if (!addr || typeof addr.email !== 'string' || addr.email.length === 0) {
    throw new Error('formatAddress: email is required')
  }
  if (!addr.name) return addr.email
  const encoded = encodeHeaderValue(addr.name)
  return `${encoded} <${addr.email}>`
}

/**
 * Encode a header value per RFC 2047 when it contains non-ASCII. Pure ASCII
 * values pass through verbatim so `Subject: Hello world` stays readable.
 */
function encodeHeaderValue(value: string): string {
  const isAscii = /^[\x00-\x7F]*$/.test(value)
  if (isAscii) return value
  const bytes = new TextEncoder().encode(value)
  const b64 = base64EncodeBytes(bytes)
  return `=?utf-8?B?${b64}?=`
}

function escapeHeaderParam(value: string): string {
  return value.replace(/"/g, '\\"')
}

function generateBoundary(): string {
  // 128 bits of entropy. Using getRandomValues keeps the boundary distinct
  // per message — critical because Gmail's server-side MIME parser rejects
  // messages where the boundary appears inside a body part.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  return `=_kubdee_${hex}`
}

// ============================================================================
// Quoted-printable — encode (outbound) + decode (inbound)
// ============================================================================

/**
 * Encode a UTF-8 string as quoted-printable per RFC 2045. Thai characters
 * (three-byte UTF-8 sequences) become `=E0=B8=81` etc. Lines are wrapped at
 * 75 chars (the RFC limit is 76 incl. soft `=` trailer).
 */
export function encodeQuotedPrintable(input: string): string {
  const bytes = new TextEncoder().encode(input)
  const out: string[] = []
  let line = ''
  const flush = () => {
    out.push(line)
    line = ''
  }
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i]
    let token: string
    if (byte === 0x0a) {
      // LF — end the line. Preserve as plain \r\n.
      flush()
      continue
    }
    if (byte === 0x0d) {
      // CR — swallow; the LF will end the line.
      continue
    }
    if (byte === 0x20 || byte === 0x09) {
      // SPACE / TAB — only encode at end of line. We buffer as-is and rely
      // on the wrap-guard below to flip trailing whitespace to `=20` / `=09`.
      token = String.fromCharCode(byte)
    } else if (byte >= 0x21 && byte <= 0x7e && byte !== 0x3d) {
      // Printable ASCII except `=` — verbatim.
      token = String.fromCharCode(byte)
    } else {
      // Everything else → `=XX`.
      token = '=' + byte.toString(16).toUpperCase().padStart(2, '0')
    }
    if (line.length + token.length > 75) {
      // Soft line break.
      out.push(line + '=')
      line = ''
    }
    line += token
  }
  if (line.length > 0) flush()
  // Encode trailing whitespace on every line as `=20` / `=09` per RFC.
  return out
    .map((l) => l.replace(/([ \t])$/g, (_m, c: string) => (c === ' ' ? '=20' : '=09')))
    .join('\r\n')
}

/**
 * Decode a quoted-printable string. Handles:
 *   - `=XX` hex escape
 *   - `=\r\n` / `=\n` soft line break (consumed)
 *   - Thai multi-byte sequences (e.g. `=E0=B8=81`) via UTF-8 decoder
 */
export function decodeQuotedPrintable(input: string): string {
  // First collapse soft line breaks so they don't pollute the hex walker.
  const withoutSoft = input.replace(/=\r?\n/g, '')
  const bytes: number[] = []
  for (let i = 0; i < withoutSoft.length; i++) {
    const ch = withoutSoft[i]
    if (ch === '=') {
      const hex = withoutSoft.slice(i + 1, i + 3)
      if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
        bytes.push(parseInt(hex, 16))
        i += 2
        continue
      }
      // Malformed escape — preserve the `=` as-is.
      bytes.push(0x3d)
      continue
    }
    if (ch === '\r') continue
    bytes.push(ch.charCodeAt(0))
  }
  return new TextDecoder('utf-8').decode(new Uint8Array(bytes))
}

// ============================================================================
// Parse — raw MIME text → EmailFull
// ============================================================================

interface ParsedPart {
  headers: Record<string, string>
  /** Canonical raw header lines (preserves first occurrence of duplicates). */
  rawHeaders: string[]
  body: string
  /** Child parts for multipart bodies. */
  parts: ParsedPart[]
  /** Helper lookup on Content-Type params. */
  contentType?: string
  charset?: string
  encoding?: string
  boundary?: string
  disposition?: string
  filename?: string
  contentId?: string
}

/**
 * Parse a raw MIME string (e.g. from a Gmail `format=raw` response OR
 * reconstructed from `payload`) into an `EmailFull`. The `messageId` +
 * `threadId` must be supplied by the caller — the raw MIME does not carry
 * them (Gmail assigns these outside the envelope).
 */
export function parseMimeMessage(
  raw: string,
  hint: { messageId: string; threadId: string; labelIds?: string[]; isUnread?: boolean; snippet?: string; internalDate?: string } =
    { messageId: '', threadId: '' },
): EmailFull {
  const root = parseMimePart(raw)
  const headers = root.headers
  const subject = decodeHeaderValue(headers['subject']) ?? ''
  const from = parseAddressList(headers['from'])[0] ?? { email: '' }
  const to = parseAddressList(headers['to'])
  const cc = headers['cc'] ? parseAddressList(headers['cc']) : undefined
  const bcc = headers['bcc'] ? parseAddressList(headers['bcc']) : undefined
  const replyTo = headers['reply-to'] ? parseAddressList(headers['reply-to']) : undefined
  const inReplyTo = headers['in-reply-to']?.trim() || undefined
  const references = headers['references']
    ? headers['references'].split(/\s+/).filter((s) => s.length > 0)
    : undefined

  const body: { text?: string; html?: string } = {}
  const attachments: EmailAttachment[] = []
  collectBodies(root, body, attachments)

  const receivedAt = hint.internalDate
    ? new Date(Number(hint.internalDate)).toISOString()
    : headers['date']
      ? new Date(headers['date']).toISOString()
      : new Date(0).toISOString()

  const isUnread = hint.isUnread ?? (hint.labelIds?.includes('UNREAD') ?? false)

  return {
    messageId: hint.messageId,
    threadId: hint.threadId,
    from,
    to,
    subject,
    snippet: hint.snippet ?? (body.text ?? body.html ?? '').slice(0, 200),
    receivedAt,
    isUnread,
    labels: hint.labelIds,
    cc,
    bcc,
    replyTo,
    body,
    attachments,
    headers,
    inReplyTo,
    references,
  }
}

// Round 1 H1 / H2 fix: bound the MIME tree we're willing to walk.
//
// Without these caps a crafted message can:
//   - blow the Workers V8 stack (~1k frames) via deeply nested multipart/*
//   - exhaust RAM by emitting thousands of tiny `--boundary` markers
//
// 20 levels is generous for legitimate mail (nested forwards rarely exceed 3).
// 500 parts per level covers high-attachment-count newsletters with headroom.
const MIME_MAX_DEPTH = 20
const MIME_MAX_PARTS_PER_LEVEL = 500

function parseMimePart(raw: string, depth = 0): ParsedPart {
  // Find end-of-headers (CRLF CRLF or LF LF).
  const boundaryIdx = findHeaderBodySplit(raw)
  const headerText = raw.slice(0, boundaryIdx)
  const body = raw.slice(boundaryIdx).replace(/^\r?\n\r?\n/, '')

  const { headers, rawHeaders } = parseHeaders(headerText)
  const part: ParsedPart = { headers, rawHeaders, body, parts: [] }
  const ct = headers['content-type'] ?? ''
  const params = parseContentTypeParams(ct)
  part.contentType = (params.type || 'text/plain').toLowerCase()
  part.charset = (params.params['charset'] || 'utf-8').toLowerCase()
  part.encoding = (headers['content-transfer-encoding'] || '7bit').toLowerCase()
  part.boundary = params.params['boundary']
  const disp = parseContentTypeParams(headers['content-disposition'] ?? '')
  part.disposition = disp.type?.toLowerCase()
  part.filename = disp.params['filename'] || params.params['name']
  const cid = headers['content-id']?.trim()
  if (cid) {
    // Strip surrounding < > if present.
    part.contentId = cid.replace(/^<|>$/g, '')
  }

  if (part.contentType.startsWith('multipart/') && part.boundary) {
    if (depth >= MIME_MAX_DEPTH) {
      // Stop recursion — leave `parts` empty. The message is malformed or
      // adversarial; dropping the sub-tree is safer than exhausting the stack.
      // The surrounding body string is still available on `part.body` for
      // diagnostic access.
      return part
    }
    part.parts = splitMultipart(body, part.boundary).map((chunk) =>
      parseMimePart(chunk, depth + 1),
    )
  }

  return part
}

function findHeaderBodySplit(raw: string): number {
  const crlf = raw.indexOf('\r\n\r\n')
  const lf = raw.indexOf('\n\n')
  if (crlf >= 0 && (lf < 0 || crlf < lf)) return crlf
  if (lf >= 0) return lf
  return raw.length
}

function parseHeaders(text: string): { headers: Record<string, string>; rawHeaders: string[] } {
  const headers: Record<string, string> = {}
  const rawHeaders: string[] = []
  // Unfold continuation lines (RFC 5322 §2.2.3) — a line starting with
  // whitespace belongs to the previous header.
  const unfolded = text.replace(/\r?\n[ \t]+/g, ' ')
  const lines = unfolded.split(/\r?\n/)
  for (const line of lines) {
    if (!line) continue
    const idx = line.indexOf(':')
    if (idx <= 0) continue
    const name = line.slice(0, idx).trim()
    const value = line.slice(idx + 1).trim()
    rawHeaders.push(`${name}: ${value}`)
    const key = name.toLowerCase()
    // Preserve first occurrence for lookups that expect a single value.
    if (!(key in headers)) headers[key] = value
  }
  return { headers, rawHeaders }
}

function parseContentTypeParams(header: string): { type?: string; params: Record<string, string> } {
  if (!header) return { params: {} }
  const parts = header.split(';').map((p) => p.trim())
  const type = parts.shift()
  const params: Record<string, string> = {}
  for (const p of parts) {
    const eq = p.indexOf('=')
    if (eq < 0) continue
    const key = p.slice(0, eq).trim().toLowerCase()
    let value = p.slice(eq + 1).trim()
    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1).replace(/\\"/g, '"')
    }
    params[key] = value
  }
  return { type, params }
}

function splitMultipart(body: string, boundary: string): string[] {
  const marker = `--${boundary}`
  const closeMarker = `--${boundary}--`
  // Find the first boundary — everything before it is preamble, discarded.
  const startIdx = body.indexOf(marker)
  if (startIdx < 0) return []
  const endIdx = body.indexOf(closeMarker)
  const tailLimit = endIdx >= 0 ? endIdx : body.length
  const body2 = body.slice(startIdx, tailLimit)
  const raw = body2.split(marker)
  const parts: string[] = []
  for (const chunk of raw) {
    if (!chunk || chunk === '\r\n' || chunk === '\n') continue
    // Each chunk begins with \r\n or \n (from the boundary line) — strip.
    const stripped = chunk.replace(/^\r?\n/, '').replace(/\r?\n$/, '')
    if (stripped.length === 0) continue
    parts.push(stripped)
    // Round 1 H2 fix: bound parts per level. A crafted message with
    // thousands of boundary markers could otherwise allocate an unbounded
    // string[] + one parseMimePart call each. Extra chunks are dropped.
    if (parts.length >= MIME_MAX_PARTS_PER_LEVEL) break
  }
  return parts
}

function collectBodies(
  part: ParsedPart,
  body: { text?: string; html?: string },
  attachments: EmailAttachment[],
): void {
  if (part.parts.length > 0) {
    for (const child of part.parts) collectBodies(child, body, attachments)
    return
  }
  const ct = part.contentType ?? 'text/plain'
  const disposition = part.disposition ?? ''
  const isAttachment =
    disposition === 'attachment' ||
    (disposition === 'inline' && !!part.filename) ||
    (!ct.startsWith('text/') && !ct.startsWith('multipart/'))

  if (isAttachment) {
    const data = decodePartBytes(part.body, part.encoding)
    attachments.push({
      filename: part.filename || 'attachment',
      contentType: ct,
      data: data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer,
      contentId: part.contentId,
      size: data.byteLength,
    })
    return
  }

  const decoded = decodePartText(part.body, part.encoding, part.charset)
  if (ct === 'text/html' && !body.html) body.html = decoded
  else if (!body.text) body.text = decoded
}

function decodePartBytes(body: string, encoding?: string): Uint8Array {
  const enc = (encoding || '7bit').toLowerCase()
  if (enc === 'base64') {
    // Base64 bodies may be line-wrapped; strip whitespace before decode.
    const clean = body.replace(/\s+/g, '')
    // Tolerate url-safe variant.
    return base64UrlDecodeToBytes(clean)
  }
  if (enc === 'quoted-printable') {
    const text = decodeQuotedPrintable(body)
    return new TextEncoder().encode(text)
  }
  // Issue #254 M1: 7bit / 8bit / binary — raw bytes.
  // The previous implementation called `new TextEncoder().encode(body)`, which
  // UTF-8-encodes the string. For a binary attachment declared as `8bit` or
  // `binary` the parser upstream has already placed each raw byte into one
  // UTF-16 code unit (`String.fromCharCode(byte)`), so any byte > 0x7F would
  // round-trip as a multi-byte UTF-8 sequence — corrupting the file.
  //
  // Walk the string char-by-char instead, masking to 8 bits so the caller
  // gets a faithful byte-for-byte reconstruction of the 7bit/8bit/binary body.
  if (enc === '7bit' || enc === '8bit' || enc === 'binary') {
    const bytes = new Uint8Array(body.length)
    for (let i = 0; i < body.length; i++) {
      bytes[i] = body.charCodeAt(i) & 0xff
    }
    return bytes
  }
  // Unknown encoding — fall back to UTF-8 encode so at least text-y bodies
  // still work. Unknown encodings are extremely rare in the wild.
  return new TextEncoder().encode(body)
}

function decodePartText(body: string, encoding: string | undefined, charset: string | undefined): string {
  const enc = (encoding || '7bit').toLowerCase()
  if (enc === 'base64') {
    const clean = body.replace(/\s+/g, '')
    const bytes = base64UrlDecodeToBytes(clean)
    try {
      return new TextDecoder(charset || 'utf-8').decode(bytes)
    } catch {
      return new TextDecoder('utf-8').decode(bytes)
    }
  }
  if (enc === 'quoted-printable') {
    return decodeQuotedPrintable(body)
  }
  // 7bit / 8bit — already text in the declared charset. We assume utf-8 for
  // safety; Thai senders that use Windows-874 (rare) would need explicit
  // handling if we ever observe it.
  return body
}

// ============================================================================
// Header value + address parsing
// ============================================================================

/**
 * Decode an RFC 2047 encoded-word header value (`=?utf-8?B?...?=` or
 * `=?utf-8?Q?...?=`). Leaves plain ASCII strings unchanged.
 */
export function decodeHeaderValue(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  return raw.replace(
    /=\?([^?]+)\?([BbQq])\?([^?]+)\?=/g,
    (_match: string, charsetRaw: string, encoding: string, data: string) => {
      const charset = charsetRaw.toLowerCase()
      if (encoding.toUpperCase() === 'B') {
        const bytes = base64UrlDecodeToBytes(
          data.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
        )
        try {
          return new TextDecoder(charset).decode(bytes)
        } catch {
          return new TextDecoder('utf-8').decode(bytes)
        }
      }
      // Q-encoding — like QP but `_` encodes a space.
      const text = data.replace(/_/g, ' ')
      return decodeQuotedPrintable(text)
    },
  )
}

/**
 * Parse an address-list header (e.g. `To: "Alice" <a@x>, Bob <b@y>`).
 * Returns an array of `EmailAddress`. Falls back to a single entry when the
 * value looks like a bare email.
 */
export function parseAddressList(raw: string | undefined): EmailAddress[] {
  if (!raw) return []
  const decoded = decodeHeaderValue(raw) ?? raw
  const out: EmailAddress[] = []
  // Split on commas that aren't inside quotes. Simple state machine.
  const parts: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < decoded.length; i++) {
    const c = decoded[i]
    if (c === '"') inQuotes = !inQuotes
    if (c === ',' && !inQuotes) {
      parts.push(current.trim())
      current = ''
      continue
    }
    current += c
  }
  if (current.trim().length > 0) parts.push(current.trim())

  for (const entry of parts) {
    const angle = entry.match(/^(.*?)<([^>]+)>\s*$/)
    if (angle) {
      const name = angle[1].trim().replace(/^"|"$/g, '').trim()
      out.push(name ? { email: angle[2].trim(), name } : { email: angle[2].trim() })
    } else if (entry.length > 0) {
      out.push({ email: entry })
    }
  }
  return out
}
